// src/contracts/components/LifecycleControls.js
// Minimal button row in two visual groups:
//   [ Preview ]  |  [ Generate · Upload signed · Discard ]
// Save/Download were intentionally removed — autosave covers persistence,
// and Preview's native browser tab gives you in-page download via Cmd+S.

import React, { useRef, useState } from 'react';
import { FileDown, CloudUpload, Eye, Trash2 } from 'lucide-react';

export default function LifecycleControls({
  status,
  onPreview,
  onGenerate,
  onUploadSigned,
  onDiscard,
  busy,
}) {
  const fileRef = useRef(null);
  const [pickerBusy, setPickerBusy] = useState(false);

  const handleSigned = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPickerBusy(true);
    try { await onUploadSigned(f); }
    finally {
      setPickerBusy(false);
      e.target.value = '';
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Group 1: daily-driver action — open the PDF preview in a new tab */}
      <button
        onClick={onPreview}
        disabled={busy}
        className="px-3 py-2 border border-zinc-700 hover:bg-zinc-800 disabled:opacity-40 disabled:cursor-not-allowed text-zinc-200 rounded-lg text-sm inline-flex items-center gap-2 transition-colors"
        title="Preview the PDF in a new tab (save to disk from the browser if needed)"
      >
        <Eye className="w-4 h-4" />
        Preview
      </button>

      {/* Vertical divider between daily-use action and lifecycle actions */}
      <div className="h-6 w-px bg-zinc-800 mx-1" aria-hidden />

      {/* Group 2: lifecycle — commits to Dropbox, status changes, draft delete */}
      <button
        onClick={onGenerate}
        disabled={busy}
        className="px-4 py-2 bg-indigo-700 hover:bg-indigo-600 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-lg text-sm font-medium inline-flex items-center gap-2 transition-colors"
      >
        <FileDown className="w-4 h-4" />
        {status === 'Sent' || status === 'Signed' ? 'Regenerate PDF' : 'Generate PDF & mark sent'}
      </button>

      <button
        onClick={() => fileRef.current?.click()}
        disabled={pickerBusy || status === 'Draft'}
        className="px-3 py-2 border border-zinc-700 hover:bg-zinc-800 disabled:opacity-30 disabled:cursor-not-allowed text-zinc-200 rounded-lg text-sm inline-flex items-center gap-2 transition-colors"
        title={status === 'Draft' ? 'Generate the unsigned PDF first' : 'Upload the signed copy'}
      >
        <CloudUpload className="w-4 h-4" />
        Upload signed copy
      </button>
      <input ref={fileRef} type="file" accept="application/pdf" onChange={handleSigned} className="hidden" />

      <button
        onClick={onDiscard}
        className="px-3 py-2 text-red-300 hover:bg-red-900/30 rounded-lg text-sm inline-flex items-center gap-2 transition-colors"
        title="Delete this draft"
      >
        <Trash2 className="w-4 h-4" />
        Discard
      </button>
    </div>
  );
}
